import { useBirthWish } from '@/OneLiners';
import {
    ActionStatusHelper,
    DomainFormHook,
    DomainHookProps,
    ActionResponse,
    useFormLifeCycle,
    DOMAIN_CONFIGS
} from "@/Shared";


import { BirthWishFormViewModel } from '../ViewModels';
import { useBirthWishEnricher } from ".";
import { BirthWishValidationSchema } from "../BirthWishFormValidation";
import { BirthWishModel } from "../BirthWishModel";


/**
 * useBirthWishForm hook
 *
 * Domain-specific form hook responsible for creating and updating BirthWish entities.
 * Acts as the orchestration layer between:
 * - domain data access and enrichment,
 * - form view model initialization,
 * - submission lifecycle (loading / success / error),
 * - UI feedback (toasts, modal closing),
 * - and navigation.
 *
 * Responsibilities:
 * - Initialize the BirthWish form view model (create or edit mode).
 * - Handle form submission and map values to FormData.
 * - Track create/update responses and provide user feedback.
 * - Redirect or close modal after successful submission.
 *
 * Author: Samano CASTRE
 * Date: July 2025
 */
export const useBirthWishForm = (props: DomainHookProps): DomainFormHook => {
    const { extraArgs, permissionHook } = props;
    const { contactId } = extraArgs ?? {};
    const { domainType } = DOMAIN_CONFIGS.BirthWish;
    /**
         * BirthWish domain operations and related backend state.
         * Exposes CRUD actions, current entity fetching and associated responses.
         */
    const birthWishHook = useBirthWish();

    /**
        * BirthWish enrichment layer.
        * Provides additional data (users, computed fields, etc.)
        * required to build the form view model.
        */
    const {
        userMap,
        contactMap,
        enricherResponse,
        getEnrichedBirthWish,
    } = useBirthWishEnricher();


    /**
    * Aggregated response representing the global initialization state.
    * Combines:
    * - birthWish domain state
    * - permission loading state
    * - enrichment layer state
    */
    const response: ActionResponse = {
        status: ActionStatusHelper.combine(
            birthWishHook.stateResponse.status,
            enricherResponse.status,
            permissionHook.stateResponse.status
        ),
        error: birthWishHook.stateResponse.error ??
            enricherResponse.error
    };

    const lifeCycleHook = useFormLifeCycle({
        response,
        domainType,
        props,
        entityHook: birthWishHook,
        buildVM: (currentEntity: BirthWishModel) => {
            const contact = contactId ? contactMap.get(contactId) : undefined;
            return new BirthWishFormViewModel(
                contact,
                getEnrichedBirthWish(currentEntity)
            )
        }
    });


    /**
     * Public API exposed to the component layer.
     * Only data and actions strictly required by the UI are returned.
     */
    return {
        // Form
        ...lifeCycleHook,
        ValidationSchema: BirthWishValidationSchema()
    }
}